import is from '@sindresorhus/is';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

const SCROLL_TOLERANCE_PX = 10;

const isNearBottom = (node: HTMLElement): boolean => {
    const { clientHeight, scrollHeight, scrollTop } = node;
    return scrollHeight - clientHeight <= scrollTop + SCROLL_TOLERANCE_PX;
};

export const useScrollToBottom = (ref: RefObject<HTMLElement | null>, deps: readonly unknown[]): void => {
    const userScrolledAwayRef = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (is.null(node)) return;

        const handleScroll = (): void => {
            userScrolledAwayRef.current = !isNearBottom(node);
        };

        node.addEventListener('scroll', handleScroll, { passive: true });
        return () => node.removeEventListener('scroll', handleScroll);
    }, [ref]);

    useEffect(() => {
        const node = ref.current;
        if (is.null(node)) return;

        if (isNearBottom(node) || !userScrolledAwayRef.current) {
            node.scrollTop = node.scrollHeight;
        }
        // biome-ignore lint/correctness/useExhaustiveDependencies: caller passes the values that should trigger the scroll.
    }, deps);
};
